import type moment from "moment";

import { type IBaseEntity } from "types/apiResponse/apiResponse";
import { EmptyResponseEntity } from "types/emptyResponseEntity";

import { APIHandler, type IBlobResponse } from "utilities/apiHandler";
import {
    ExcelExportRequest,
    type GridRequest,
    type IJobParamGridRequestData,
    type ListEntityType,
    mapPagingDataToRequest,
    PagingData,
} from "utilities/list/list.types";

import { getDefaultPageSize } from "commonComponents/btWrappers/BTPagination/BTPagination";
import { BulkActionResponse } from "commonComponents/entity/bulkAction/BulkAction/BulkAction";
import { LineItemIdsToInvoiceResponse } from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { AccountingEntityTypes } from "commonComponents/financial/Common/Accounting.types";
import { type IListWithGridHandler } from "commonComponents/utilities/Grid/GridContainer.types";

import { type IFilterFormValues } from "entity/filters/Filter/Filter.api.types";
import { getNullableFilterString } from "entity/filters/filters.utils";

import {
    ApplyLienWaiverResponse,
    BillGridEntity,
    CanSendEntitiesToAccountingResponse,
    CopyBillsResponse,
    type IBillsMassDeleteRequest,
    MarkReadyForPaymentResponse,
    MassEmailLienWaiversDataResponse,
    MassUpdateAccountingStatusResponse,
    MassVoidAlertInfo,
    VoidSelectedPaymentsResponse,
} from "./BillList.api.types";

export interface IBillListHandler extends IListWithGridHandler<BillGridEntity> {
    download(
        jobIds: number[],
        gridRequest?: GridRequest,
        filterValues?: IFilterFormValues,
    ): Promise<IBlobResponse>;
    getBillLineItemIds(billIds: number[], jobId: number): Promise<LineItemIdsToInvoiceResponse>;
    voidSelectedPayments(
        billIds: number[],
        shouldVoidInAccounting?: boolean,
    ): Promise<VoidSelectedPaymentsResponse>;
    getMassVoidAlertInfo(billIds: number[]): Promise<MassVoidAlertInfo>;
    markReadyForPayment(billIds: number[]): Promise<MarkReadyForPaymentResponse>;
    getDocumentInstancesForApprovedLienWaivers(
        billIds: number[],
        jobId: number,
    ): Promise<MassEmailLienWaiversDataResponse>;
    updateAccountingStatus(billIds: number[]): Promise<MassUpdateAccountingStatusResponse>;
    canSendToAccounting(billIds: number[]): Promise<CanSendEntitiesToAccountingResponse>;
    deleteBills(request: IBillsMassDeleteRequest): Promise<BulkActionResponse>;
    copyBills(
        builderId: number,
        billIds: number[],
        destinationJobId: number,
        copyBillDocuments: boolean,
        newDeadlineDate?: moment.Moment,
    ): Promise<CopyBillsResponse>;
    getApplyLienWaiverInfo(bills: IBaseEntity[]): Promise<ApplyLienWaiverResponse>;
    applyLienWaivers(bills: IBaseEntity[], lienWaiverFormId: number): Promise<EmptyResponseEntity>;
}

export class BillListHandler implements IBillListHandler {
    async get(
        entityType: ListEntityType,
        signal: AbortSignal,
        gridRequest?: GridRequest,
        selectedJobIds?: number[],
        filterValues?: IFilterFormValues,
        pagingData?: PagingData,
    ): Promise<BillGridEntity> {
        const pagingDataForRequest = pagingData
            ? pagingData
            : new PagingData(getDefaultPageSize(entityType));
        const reqData: IJobParamGridRequestData = {
            gridRequest: gridRequest,
            pagingData: mapPagingDataToRequest(pagingDataForRequest, false),
            filters: getNullableFilterString(filterValues),
            jobIds: selectedJobIds,
        };

        return await APIHandler(`/api/v1/bills/grid`, {
            method: "POST",
            data: reqData,
            responseType: BillGridEntity,
            signal,
        });
    }

    async download(
        jobIds: number[],
        gridRequest?: GridRequest,
        filterValues?: IFilterFormValues,
    ): Promise<IBlobResponse> {
        const reqData = new ExcelExportRequest({
            gridRequest,
            filters: getNullableFilterString(filterValues),
            jobIds,
        });
        return await APIHandler("/api/v1/bills/ExportToExcel", reqData);
    }

    async getBillLineItemIds(
        billIds: number[],
        jobId: number,
    ): Promise<LineItemIdsToInvoiceResponse> {
        return await APIHandler("/api/v1/bills/GetBillLineItemIds", {
            method: "GET",
            data: { billIds, jobId },
            responseType: LineItemIdsToInvoiceResponse,
        });
    }

    async getMassVoidAlertInfo(billIds: number[]): Promise<MassVoidAlertInfo> {
        return await APIHandler("/api/v1/bills/GetMassVoidAlertInfo", {
            method: "POST",
            data: billIds,
            responseType: MassVoidAlertInfo,
        });
    }

    async voidSelectedPayments(
        billIds: number[],
        shouldVoidInAccounting?: boolean,
    ): Promise<VoidSelectedPaymentsResponse> {
        return await APIHandler("/api/v1/bills/VoidPayments", {
            method: "PUT",
            data: { billIds, shouldVoidInAccounting },
            responseType: VoidSelectedPaymentsResponse,
        });
    }

    async markReadyForPayment(billIds: number[]): Promise<MarkReadyForPaymentResponse> {
        return await APIHandler("/api/v1/bills/MarkReadyForPayment", {
            method: "PUT",
            data: {
                billIds,
            },
            responseType: MarkReadyForPaymentResponse,
        });
    }

    async getDocumentInstancesForApprovedLienWaivers(
        billIds: number[],
        jobId: number,
    ): Promise<MassEmailLienWaiversDataResponse> {
        return await APIHandler("/api/v1/bills/GetDocInstancesForLienWaivers", {
            method: "GET",
            responseType: MassEmailLienWaiversDataResponse,
            data: { jobId },
            headers: { billIds },
        });
    }

    async updateAccountingStatus(billIds: number[]): Promise<MassUpdateAccountingStatusResponse> {
        return await APIHandler("/api/v1/bills/SyncAccountingStatus", {
            method: "PUT",
            data: { billIds },
            responseType: MassUpdateAccountingStatusResponse,
        });
    }

    async canSendToAccounting(billIds: number[]): Promise<CanSendEntitiesToAccountingResponse> {
        return await APIHandler("/api/Accounting/CanSendEntitiesToAccounting", {
            method: "GET",
            data: {
                accountingEntityType: AccountingEntityTypes.Bill,
            },
            headers: {
                accountingEntityIds: billIds,
            },
            responseType: CanSendEntitiesToAccountingResponse,
        });
    }

    async sendToAccounting(
        billIds: number[],
        markAsBillableToOwner: boolean,
        invoiceDate: moment.Moment | undefined,
    ): Promise<BulkActionResponse> {
        return await APIHandler("/api/Accounting/SendEntitiesToAccounting", {
            method: "PUT",
            data: {
                accountingEntityType: AccountingEntityTypes.Bill,
                accountingEntityIds: billIds,
                billToOwner: markAsBillableToOwner,
                invoiceDate: invoiceDate,
            },
            responseType: BulkActionResponse,
        });
    }

    async deleteBills(request: IBillsMassDeleteRequest): Promise<BulkActionResponse> {
        return await APIHandler("/api/v1/bills/BulkDeleteBills", {
            method: "DELETE",
            data: request,
            responseType: BulkActionResponse,
        });
    }

    async copyBills(
        builderId: number,
        billIds: number[],
        destinationJobId: number,
        copyBillDocuments: boolean,
        newDeadlineDate?: moment.Moment,
    ): Promise<CopyBillsResponse> {
        return await APIHandler("/api/v1/bills/CopyBills", {
            method: "POST",
            data: {
                builderId,
                billIds,
                destinationJobId,
                copyBillDocuments,
                newDeadlineDate,
            },
            responseType: CopyBillsResponse,
        });
    }

    async getApplyLienWaiverInfo(bills: IBaseEntity[]): Promise<ApplyLienWaiverResponse> {
        return await APIHandler("/api/v1/bills/ApplyLienWaiverInfo", {
            method: "POST",
            data: { selectedEntities: bills },
            responseType: ApplyLienWaiverResponse,
        });
    }

    async applyLienWaivers(
        bills: IBaseEntity[],
        lienWaiverFormId: number,
    ): Promise<EmptyResponseEntity> {
        return await APIHandler("/api/v1/bills/ApplyLienWaivers", {
            method: "PUT",
            data: { bills, lienWaiverFormId },
            responseType: EmptyResponseEntity,
        });
    }
}
